// src/components/HeroHeader.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { fetchHeroImage } from '../utils/googleApi';

const HeroHeader = () => {
  const [heroSrc, setHeroSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const heroFolderId = process.env.REACT_APP_GOOGLE_DRIVE_HERO_FOLDER_ID;
    fetchHeroImage(heroFolderId)
      .then(url => setHeroSrc(url))
      .catch(err => console.error('[LFBBQ] Hero image error:', err))
      .finally(() => setLoading(false));
  }, []);

  // Show a dark placeholder while loading or if no image configured
  if (loading || !heroSrc) {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '60vh', md: '70vh' },
          backgroundColor: '#111',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
        }}
      >
        {loading ? (
          <CircularProgress sx={{ color: 'white', mb: 3 }} />
        ) : null}

        <Typography
          variant="h2"
          sx={{
            color: 'white',
            textAlign: 'center',
            fontFamily: '"Audiowide", sans-serif',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.75rem' },
            mb: 1,
          }}
        >
          Texas Craft BBQ & Catering
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255,255,255,0.7)',
            textAlign: 'center',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 300,
            fontSize: { xs: '1rem', md: '1.25rem' },
            mb: 4,
          }}
        >
          Low & slow smoked meats crafted with passion
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            component={Link}
            to="/menu"
            variant="contained"
            sx={{
              backgroundColor: 'white',
              color: 'black',
              fontFamily: '"Audiowide", sans-serif',
              fontSize: { xs: '14px', md: '16px' },
              px: { xs: 3, md: 4 },
              py: 1.5,
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#72faff' },
              transition: 'all 0.3s ease',
            }}
          >
            View Menu
          </Button>
          <Button
            component={Link}
            to="/catering"
            variant="contained"
            sx={{
              backgroundColor: 'white',
              color: 'black',
              fontFamily: '"Audiowide", sans-serif',
              fontSize: { xs: '14px', md: '16px' },
              px: { xs: 3, md: 4 },
              py: 1.5,
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#72faff' },
              transition: 'all 0.3s ease',
            }}
          >
            Request Catering
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      {/* Background image from Drive */}
      <Box
        component="img"
        src={heroSrc}
        alt="Hero Background"
        sx={{
          width: '100%',
          height: { xs: '60vh', md: 'auto' },
          objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* Dark gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.85) 100%)',
        }}
      />

      {/* Content overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
        }}
      >
        <Typography
          variant="h2"
          sx={{
            color: 'white',
            textAlign: 'center',
            fontFamily: '"Audiowide", sans-serif',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.75rem' },
            textShadow: '0 2px 12px rgba(0,0,0,0.7)',
            mb: 1,
          }}
        >
          Texas Craft BBQ & Catering
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255,255,255,0.85)',
            textAlign: 'center',
            fontFamily: 'Roboto, sans-serif',
            fontWeight: 300,
            fontSize: { xs: '1rem', md: '1.25rem' },
            mb: 4,
            maxWidth: '600px',
            textShadow: '0 1px 6px rgba(0,0,0,0.5)',
          }}
        >
          Low & slow smoked meats crafted with passion
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            component={Link}
            to="/menu"
            variant="contained"
            sx={{
              backgroundColor: 'white',
              color: 'black',
              fontFamily: '"Audiowide", sans-serif',
              fontSize: { xs: '14px', md: '16px' },
              px: { xs: 3, md: 4 },
              py: 1.5,
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 'normal',
              '&:hover': { backgroundColor: '#72faff', color: 'black' },
              transition: 'all 0.3s ease',
            }}
          >
            View Menu
          </Button>
          <Button
            component={Link}
            to="/catering"
            variant="contained"
            sx={{
              backgroundColor: 'white',
              color: 'black',
              fontFamily: '"Audiowide", sans-serif',
              fontSize: { xs: '14px', md: '16px' },
              px: { xs: 3, md: 4 },
              py: 1.5,
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 'normal',
              '&:hover': { backgroundColor: '#72faff' },
              transition: 'all 0.3s ease',
            }}
          >
            Request Catering
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroHeader;
